import React, { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import Button from '../ui/Button';
import Container from '../ui/Container';

const Platform = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section id="platform" className="py-16 md:py-24 bg-gradient-to-br from-blue-900 to-blue-800 text-white relative overflow-hidden">
      {/* Decorative dots */}
      <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4"></div>
      
      <Container>
        <div ref={sectionRef} className="opacity-0 transition-opacity duration-1000">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-orange-600 text-white px-4 py-1 rounded-full text-sm font-medium mb-4">
                Our Platform
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">DriversOnCall</h2>
              <p className="text-blue-100 mb-6 text-lg">
                Connect with reliable drivers whenever you need them. DriversOnCall is our innovative platform that connects users with professional drivers on-demand.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-white/10 p-2 rounded-full mr-4">
                    <ArrowRight className="text-orange-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium">Instant Booking</h4>
                    <p className="text-blue-100 text-sm">Book a driver with just a few taps, available 24/7</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white/10 p-2 rounded-full mr-4">
                    <ArrowRight className="text-orange-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium">Verified Professionals</h4>
                    <p className="text-blue-100 text-sm">All drivers are background-checked and verified</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white/10 p-2 rounded-full mr-4">
                    <ArrowRight className="text-orange-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium">Flexible Options</h4>
                    <p className="text-blue-100 text-sm">Hourly, daily, or weekly driver services</p>
                  </div>
                </div>
              </div>
              
              <a 
                href="https://driversoncall.co" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button variant="secondary" size="lg" className="group">
                  Visit DriversOnCall
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
              </a>
            </div>
            
            <div className="relative">
              <div className="relative rounded-lg overflow-hidden shadow-2xl transform transition-transform hover:scale-[1.02]">
                <img
                  src="https://images.pexels.com/photos/7706563/pexels-photo-7706563.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                  alt="DriversOnCall platform"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                    <h3 className="text-blue-900 font-bold text-lg">DriversOnCall</h3>
                    <p className="text-gray-700">Professional drivers at your fingertips</p>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-orange-500/20 rounded-full"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-blue-500/20 rounded-full"></div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Platform;